/**
 * 스코프 taxonomy 런타임 — 순수 함수/상수만. zod 없이도 동작하므로 대시보드(브라우저) 가
 * 상대경로(`../../contracts/scope-runtime.ts`) 로 그대로 import 해서 쓴다.
 *
 * zod 가 필요한 `SCOPE_SCHEMA` 는 `scopes.ts` 에 있으며 여기 상수/함수를 재export 한다.
 */

export const RESOURCES = ["kg", "vector", "docs", "admin", "episodes", "documents", "associations"] as const
export type Resource = (typeof RESOURCES)[number]

export const ACTIONS = ["read", "write", "delete"] as const
export type Action = (typeof ACTIONS)[number]

/** MCP 서버 이름 — URL 세그먼트에 그대로 쓰이는 kebab-case. */
export const MCP_SERVER_NAME_REGEX = /^[a-z][a-z0-9-]{0,31}$/

/**
 * 역할 프리셋 — 대시보드 발급 UI 의 초기값.
 *
 * V11: `context` 리소스 제거 — 어셈블리는 consumer 책임, knowledge 는 `knowledge_search` 만 제공.
 * - `reader` 는 edge episodes 읽기도 허용 (관계 근거 조회)
 * - `editor` / `ingest` 는 attach/link/invalidate 쓰기 권한
 * - Document Versioning: `reader` 는 documents:read, `editor`/`ingest` 는 documents:write 추가
 */
export const ROLE_PRESETS = {
	reader: ["kg:read", "docs:read", "episodes:read", "documents:read", "associations:read"],
	editor: [
		"kg:read",
		"kg:write",
		"docs:read",
		"episodes:read",
		"episodes:write",
		"documents:read",
		"documents:write",
		"associations:read",
		"associations:write"
	],
	ingest: ["kg:write", "vector:write", "episodes:write", "documents:write", "associations:write"],
	admin: ["*"]
} as const satisfies Readonly<Record<string, ReadonlyArray<string>>>

export type RolePreset = keyof typeof ROLE_PRESETS

/**
 * 스코프가 필요한 권한을 만족하는지 검사.
 * - `*` 는 모든 required 를 통과
 * - `kg:*` 는 `kg:read` / `kg:write` / `kg:delete` 를 만족
 * - 정확 매칭도 통과
 */
export function scopeSatisfies(granted: ReadonlyArray<string>, required: string): boolean {
	if (granted.includes("*")) return true
	if (granted.includes(required)) return true
	const [resource] = required.split(":")
	if (resource && granted.includes(`${resource}:*`)) return true
	return false
}

/** 여러 required 중 하나라도 만족되면 true (OR). */
export function scopeSatisfiesAny(granted: ReadonlyArray<string>, required: ReadonlyArray<string>): boolean {
	return required.some((r) => scopeSatisfies(granted, r))
}
